import { Processor, EQProcessor } from "./Processor";


/**
 * AudioEnv class manages an audio playback environment with support for custom processors
 * like EQ and others. It initializes an audio context, sets up audio nodes, and provides
 * methods for playing, pausing, adjusting volume, and managing a chain of audio processors.
 */
class AudioEnv {
  #audioElement;
  #audioContext;
  #source;
  #gainNode;
  #processors;
  #analyser
  #analysisData;
  #audioBuffer = null

  /**
   * Constructor initializes the audio environment with a source URL for the audio,
   * sets up an AudioContext, a gain node for volume control, and prepares an array
   * to store audio processors.
   * 
   * @param {string} audioSrc - The source URL for the audio element.
   */
  constructor(audioSrc) {
    // Create and set up an audio element for playback, enabling cross-origin access
    this.#audioElement = new Audio(audioSrc);
    this.#audioElement.crossOrigin = 'anonymous';

    // Initialize the audio context for managing audio nodes and processing
    this.#audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.#source = this.#audioContext.createMediaElementSource(this.#audioElement);

    // Set up a gain node to control global volume, connecting source to gain, then destination
    this.#gainNode = this.#audioContext.createGain();
    this.#source.connect(this.#gainNode);
    this.#gainNode.connect(this.#audioContext.destination);

    // Initialize AnalyserNode
    this.#analyser = this.#audioContext.createAnalyser();
    this.#analyser.fftSize = 16384 / 2;
    this.#analyser.smoothingTimeConstant = 0.5;
    this.#analysisData = new Uint8Array(this.#analyser.frequencyBinCount);

    // Connect analyser to the gain node
    this.#gainNode.connect(this.#analyser);

    // Initialize processors array and play status in the audio chain
    this.#processors = [];    // Load audio buffer for visualization

    this.loadAudioBuffer(audioSrc);
  }

  async loadAudioBuffer(audioSrc) {
    try {
      const response = await fetch(audioSrc);
      const arrayBuffer = await response.arrayBuffer();
      this.#audioBuffer = await this.#audioContext.decodeAudioData(arrayBuffer);
    } catch (error) {
      console.error("Error loading audio buffer:", error);
    }
  }

  getRawAudioData() {
    return new Promise((resolve, reject) => {
      if (this.#audioBuffer) {
        resolve(this.#audioBuffer.getChannelData(0)); // Return raw PCM data if buffer is already loaded
      } else {
        // Wait for the buffer to load
        const checkBuffer = () => {
          if (this.#audioBuffer) {
            resolve(this.#audioBuffer.getChannelData(0));
          } else {
            setTimeout(checkBuffer, 100); // Check again after a short delay
          }
        };
        checkBuffer();
      }
    });
  }
  /**
   * Returns the current audio context instance.
   * @returns {AudioContext} The audio context of the environment.
   */
  getCtx() {
    return this.#audioContext;
  }

  getAudioElement() {
    return this.#audioElement;
  }

  getWaveformData() {
    this.#analyser.getByteTimeDomainData(this.#analysisData);
    return this.#analysisData;
  }

  getAnalyserNode() {
    return this.#analyser;
  }

  getAnalysisData() {
    this.#analyser.getByteFrequencyData(this.#analysisData);
    return this.#analysisData;
  }

  /**
   * Sets the global volume by adjusting the gain node.
   * @param {number} value - The desired volume level (gain value).
   */
  setGain(value) {
    this.#gainNode.gain.value = value;
  }

  getProcessors() {
    return this.#processors;
  }

  /**
   * Plays the audio; resumes the audio context if it's suspended.
   */
  play() {
    if (this.#audioContext.state === 'suspended') {
      this.#audioContext.resume();
    }
    this.#audioElement.play();
  }

  /**
   * Pauses the audio playback.
   */
  pause() {
    this.#audioElement.pause();
  }

  _connectNodes(source, target) {
    // If 'source' is a Processor, we rely on its `.connect(...)` method
    if (source instanceof Processor) {
      return source.connect(target);
    }
    // Else 'source' is an AudioNode
    else if (source instanceof AudioNode) {
      return source.connect(target instanceof Processor ? target.getNode() : target);
    }
    throw new Error("Unsupported connection scenario in _connectNodes().");
  }

  _disconnectNodes(source, target) {
    if (source instanceof Processor) {
      return source.disconnect(target);
    } else if (source instanceof AudioNode) {
      return source.disconnect(target instanceof Processor ? target.getNode() : target);
    }
    throw new Error("Unsupported disconnection scenario in _connectNodes().");
  }

  _getPrevNode(index) {
    // If the chain is empty, the only valid 'index' is 0 (meaning "insert first processor").
    if (this.#processors.length === 0) {
      if (index === 0) {
        return this.#source;
      }
      throw new Error("Invalid prev node index: chain is empty and index != 0");
    }
    if (index < 0 || index >= this.#processors.length) {
      throw new Error("start search node out of range");
    }
    if (index === 0) {
      return this.#source;
    }
    return this.#processors[index - 1];
  }

  _getNextNode(index) {
    // If the chain is empty, the only valid 'index' is 0 (meaning "insert first processor"),
    if (this.#processors.length === 0) {
      if (index === 0) {
        return this.#gainNode;
      }
      throw new Error("Invalid next node index: chain is empty and index != 0");
    }
    if (index < 0 || index >= this.#processors.length) {
      throw new Error("start search node out of range");
    }
    if (index === this.#processors.length - 1) {
      return this.#gainNode;
    }
    return this.#processors[index];
  }
  /**
   * Adds a processor to the audio chain and reconnects the chain to include the new processor.
   * @param {Processor} processor - The processor instance to add.
   */
  addProcessor(index, processorType, config) {
    if (index < 0 || index > this.#processors.length) throw new Error("invalid add index");
    const newProcessor = this.createProcessor(processorType, config);
    this.#processors.splice(index, 0, newProcessor);

    const prev = this._getPrevNode(index);
    const next = this._getNextNode(index);

    this._disconnectNodes(prev, next);
    this._connectNodes(prev, newProcessor);
    this._connectNodes(newProcessor, next);

    return { index, newProcessor }
  }

  removeProcessor(index) {
    if (index < 0 || index >= this.#processors.length) throw new Error("invalid removal index");
    const removeNode = this.#processors[index]
    const prev = this._getPrevNode(index);
    const next = this._getNextNode(index);

    this._disconnectNodes(prev, removeNode);
    removeNode.disconnectAll()
    this._connectNodes(prev, next);

    this.#processors.splice(index, 1);
    return { index, removeNode }
  }

  updateProcessor(index, config) {
    let bypassChanged = false
    if (index < 0 || index >= this.#processors.length) throw new Error("invalid update index");

    if (this.#processors[index].getBypass() !== config.bypass ) bypassChanged = true
    
    this.#processors[index].update(config);

    return bypassChanged
  }


  updateAllProcessors(filters) {
    let bypassChanged = false
    // If we have fewer processors than filters, add new ones at the end
    while (this.#processors.length < filters.length) {
      const newIndex = this.#processors.length;
      const config = filters[newIndex];
      this.addProcessor(newIndex, config.processorType, config);
    }

    // If we have more processors than filters, remove extras from the end
    while (this.#processors.length > filters.length) {
      const lastIndex = this.#processors.length - 1;
      this.removeProcessor(lastIndex);
    }

    // update rest
    for (let i = 0; i < filters.length; i++) {
      let bypass = this.updateProcessor(i, filters[i])
      bypassChanged = bypassChanged !== true ? bypass : bypassChanged;
    }

    if(bypassChanged) {
      this.#reconnectAll()
    }

    return true;
  }

  /**
   * Creates a new processor based on the specified type and configuration.
   * Currently supports EQ processors, with additional processor types as TODO.
   * 
   * @param {string} processorType - The type of processor to create, e.g., 'eq'.
   * @param {Object} config - Configuration options for the processor.
   * @returns {Processor} The created processor instance.
   * @throws Will throw an error if the processor type is unknown.
   */
  createProcessor(processorType, config) {
    switch (processorType) {
      case 'eq':
        return new EQProcessor(this.#audioContext, config.type, config.frequency, config.gain, config.Q, config.detune);
      // TODO: Add cases for additional processor types as needed
      default:
        throw new Error(`Unknown processor type: ${processorType}`);
    }
  }

  getFreqResSingle(index, frequencies, magResponse, phaseResponse) {
    const frequencyResponse = new Float32Array(frequencies.length).fill(1);
    const node = this.#processors[index].getNode();

    node.getFrequencyResponse(frequencies, magResponse, phaseResponse);
    for (let j = 0; j < frequencyResponse.length; j++) {
      frequencyResponse[j] *= magResponse[j];
    }
    return frequencyResponse;
  }

  getFreqResGen(frequencies, magResponse, phaseResponse) {
    const frequencyResponse = new Float32Array(frequencies.length).fill(1);

    for (let p = 0; p < this.#processors.length; p++) {
      const node = this.#processors[p].getNode();
      node.getFrequencyResponse(frequencies, magResponse, phaseResponse);
      for (let j = 0; j < frequencyResponse.length; j++) {
        frequencyResponse[j] *= magResponse[j];
      }
    }
    return frequencyResponse;
  }

  #disconnectAll() {
    for (let i = 0; i < this.#processors.length; i++) {
      this.#processors[i].disconnectAll();
    }
    this.#source.disconnect();
    this.#gainNode.disconnect();
    this.#analyser.disconnect();
  }

  #reconnectAll() {
    // 1) Disconnect everything
    this.#disconnectAll();
  
    // 2) Rebuild chain, skipping any bypassed processors if you have that logic
    let currentNode = this.#source;
  
    // Go through each processor in order
    for (const processor of this.#processors) {
      // If you have a getBypass() method and want to skip bypassed processors:
      if (processor.getBypass()) {
        console.log('skipped')
        // Skip connecting this processor
        continue;
      }
  
      // Not bypassed → connect in series
      this._connectNodes(currentNode, processor);
      currentNode = processor;
    }
  
    // 3) Connect final node to gain
    this._connectNodes(currentNode, this.#gainNode);
  
    // 4) Gain node → destination & analyser
    this._connectNodes(this.#gainNode, this.#audioContext.destination);
    this._connectNodes(this.#gainNode, this.#analyser);
  }
  
  /**
   * Cleans up resources by stopping audio playback, disconnecting nodes, and
   * closing the audio context to release system resources.
   */
  cleanup() {
    this.#audioElement.pause();
    this.#audioElement.src = "";
    this.#disconnectAll();
    this.#audioContext.close();
    this.#processors = [];
  }
}

export default AudioEnv;
